import { useMutation, useQueryClient } from '@tanstack/react-query';

import getLibraryItemInfo from '@/requests/library/getLibraryItemInfo';
import { getProjectCredits } from '@/features/credits';
import { setAppStore, getAppStore } from '@/store';
import useAuth from '@/hooks/useAuth';
import { InputType, Screen } from '@/types';

import type { Preset, IInput, IImageInput, IVideoInput } from '@/types';

type GetOrFetchQueryParams<T> = {
  key: unknown[];
  fetcher: () => Promise<T>;
  queryClient: ReturnType<typeof useQueryClient>;
};

const getOrFetchQuery = async <T>({
  key,
  fetcher,
  queryClient,
}: GetOrFetchQueryParams<T>): Promise<T> => {
  const cached = queryClient.getQueryData<T>(key);

  if (cached !== undefined && cached !== null) {
    return cached;
  }

  const fetched = await fetcher();
  queryClient.setQueryData(key, fetched);
  return fetched;
};

// Helper to build inputs from preset, preserving previously uploaded media
const buildInputsFromPreset = (preset: Preset): IInput[] => {
  const textItems = preset.textItems ?? [];
  const uploadItems = preset.uploadItems ?? [];

  // Get previously uploaded media to preserve across preset changes
  const currentInputs = getAppStore().inputs;
  const previousImageSrc = currentInputs.find(
    (input): input is IImageInput =>
      input.type === InputType.IMAGE && !!input.src
  )?.src;
  const previousVideoSrc = currentInputs.find(
    (input): input is IVideoInput =>
      input.type === InputType.VIDEO && !!input.src
  )?.src;

  // Track which preserved media has been used
  let usedImageSrc = false;
  let usedVideoSrc = false;

  // Preserve uploaded media in new inputs
  const cleanedUploadItems = uploadItems.map((item) => {
    if (item.type === InputType.IMAGE && previousImageSrc && !usedImageSrc) {
      usedImageSrc = true;
      return { ...item, src: previousImageSrc };
    }
    if (item.type === InputType.VIDEO && previousVideoSrc && !usedVideoSrc) {
      usedVideoSrc = true;
      return { ...item, src: previousVideoSrc };
    }
    return { ...item, src: '' };
  }) as typeof uploadItems;

  return [...cleanedUploadItems, ...textItems];
};

const useLibraryItemInfoMutation = () => {
  const queryClient = useQueryClient();
  const { isAuthorized } = useAuth();

  return useMutation({
    mutationFn: async (slug: string) => {
      const presetKey = ['libraryItemInfo', slug];

      const preset = await getOrFetchQuery<Preset>({
        key: presetKey,
        queryClient,
        fetcher: async () => {
          const data = await getLibraryItemInfo(slug);
          if (!data) throw new Error('Failed to load library item');
          return data;
        },
      });

      // Set preset immediately to hide skeleton faster
      // Credits will be updated in background
      const inputs = buildInputsFromPreset(preset);
      setAppStore({
        selectedPreset: { ...preset, slug },
        inputs,
        screen: Screen.Create,
        isPresetLoading: false,
      });

      // Fetch credits in background (non-blocking)
      if (isAuthorized) {
        setAppStore({ isCreditsLoading: true });
        const creditsKey = ['projectCredits', preset.flowId];
        getOrFetchQuery<number | null>({
          key: creditsKey,
          queryClient,
          fetcher: () => getProjectCredits({ webhookId: preset.flowId }),
        })
          .then((projectCreditsCount) => {
            setAppStore({ credits: projectCreditsCount ?? 0 });
          })
          .finally(() => {
            setAppStore({ isCreditsLoading: false });
          });
      }

      return { preset, slug };
    },

    onMutate: () => {
      setAppStore({
        isPresetLoading: true,
        screen: Screen.Create,
      });
    },

    onError: () => {
      setAppStore({
        isPresetLoading: false,
        screen: Screen.Library,
      });
    },
  });
};

export default useLibraryItemInfoMutation;
